import React, { useState } from "react";
import {
  Box, Paper, Typography, IconButton, Tooltip, Select, MenuItem,
  FormControl, InputLabel, Button, Chip, Stack,
  useTheme,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import SortIcon from "@mui/icons-material/Sort";

const NavigatorEditor = ({ stations, navigator, onChange }) => {
  const theme = useTheme();
  const [selectedStation, setSelectedStation] = useState("");

  // Recompute orderNum + next-station links after any add/remove/reorder.
  const recalc = (items) =>
    items.map((item, i) => ({
      ...item,
      orderNum: i + 1,
      nextStation: items[i + 1]?.station || "END",
      nextStationName: items[i + 1]?.stationName || "End",
    }));

  // Move the stop at `index` up (-1) or down (+1), then renumber.
  const move = (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= navigator.length) return;
    const result = Array.from(navigator);
    const [removed] = result.splice(index, 1);
    result.splice(to, 0, removed);
    onChange(recalc(result));
  };

  const handleAdd = () => {
    if (!selectedStation) return;
    const station = stations.find((s) => s.id === selectedStation);
    if (!station) return;
    const alreadyAdded = navigator.find((n) => n.station === selectedStation);
    if (alreadyAdded) return;

    const newItem = {
      station: station.id,
      stationName: station.name,
      status: station.parcelStatus || "",
      message: station.message || "",
      orderNum: navigator.length + 1,
      nextStation: "",
      nextStationName: "",
    };
    onChange(recalc([...navigator, newItem]));
    setSelectedStation("");
  };

  const handleRemove = (index) => {
    onChange(recalc(navigator.filter((_, i) => i !== index)));
  };

  const unusedStations = stations.filter((s) => !navigator.find((n) => n.station === s.id));

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <SortIcon fontSize="small" color="secondary" />
        <Typography variant="subtitle1" fontWeight={600}>Navigator Sequence</Typography>
        <Chip label={`${navigator.length} stations`} size="small" variant="outlined" />
      </Stack>

      <Stack direction="row" spacing={1} mb={2}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Add station</InputLabel>
          <Select value={selectedStation} label="Add station" onChange={(e) => setSelectedStation(e.target.value)}>
            {unusedStations.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name} ({s.id})</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" color="secondary" size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAdd} disabled={!selectedStation}>
          Add
        </Button>
      </Stack>

      <Box sx={{ minHeight: 60 }}>
        {navigator.map((item, index) => {
          const station = stations.find((s) => s.id === item.station);
          const isFirst = index === 0;
          const isLast = index === navigator.length - 1;
          return (
            <Paper
              key={item.station}
              elevation={1}
              sx={{
                mb: 1,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <Stack spacing={0.25}>
                <IconButton size="small" onClick={() => move(index, -1)} disabled={isFirst} sx={{ width: 26, height: 22, borderRadius: 1.5 }}>
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => move(index, 1)} disabled={isLast} sx={{ width: 26, height: 22, borderRadius: 1.5 }}>
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Box sx={{ minWidth: 28, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ lineHeight: 1 }}>
                  #{item.orderNum}
                </Typography>
              </Box>

              <Box flex={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight={600}>{item.stationName}</Typography>
                  <Chip label={item.station} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 18 }} />
                  {station?.isSortStation && <Chip label="Sort" size="small" color="info" sx={{ height: 18, fontSize: "0.68rem" }} />}
                  {station?.finalStation && <FlagIcon fontSize="small" color="success" />}
                </Stack>

                {!isLast && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    → Next: <strong>{navigator[index + 1]?.stationName}</strong>
                  </Typography>
                )}
                {isLast && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                    → Final destination (END)
                  </Typography>
                )}
              </Box>

              <Tooltip title="Remove from route">
                <IconButton size="small" onClick={() => handleRemove(index)} sx={{ color: theme.palette.text.secondary, "&:hover": { color: theme.palette.error.main } }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          );
        })}
      </Box>

      {navigator.length === 0 && (
        <Box textAlign="center" py={4} color="text.secondary">
          <Typography variant="body2">No stations added yet. Add stations above to build the route.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default NavigatorEditor;
